import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// Excalidraw 白板渲染效果 - 手绘风格 + 教育内容
export const ExcalidrawRenderer: React.FC<{
  title?: string;
  content?: string[];
  primaryColor?: string;
  backgroundColor?: string;
}> = ({
  title = 'Excalidraw Style',
  content = ['Hand-drawn animations', 'Educational content', 'Visual learning'],
  primaryColor = '#1e1e1e',
  backgroundColor = '#fef3c7',
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 手绘风格效果（抖动 + 不规则线条）
  const getHandDrawnPath = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    roughness: number = 5
  ) => {
    const points = [];
    const steps = 20;

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = x1 + (x2 - x1) * t + (Math.random() - 0.5) * roughness;
      const y = y1 + (y2 - y1) * t + (Math.random() - 0.5) * roughness;
      points.push(`${i === 0 ? 'M' : 'L'} ${x} ${y}`);
    }

    return points.join(' ');
  };

  // 标题淡入
  const titleProgress = interpolate(frame, [0, 30], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 内容逐行显示
  const contentProgress = content.map((_, i) =>
    interpolate(frame, [30 + i * 20, 50 + i * 20], [0, 1], {
      extrapolateRight: 'clamp',
    })
  );

  // 手绘框动画
  const boxProgress = interpolate(frame, [90, 130], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill style={{ backgroundColor, overflow: 'hidden' }}>
      {/* 白板纹理 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: 0.3,
          backgroundImage: `
            radial-gradient(circle, #00000011 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px',
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          top: 100,
          display: 'flex',
          justifyContent: 'center',
          opacity: titleProgress,
        }}
      >
        <div
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            color: primaryColor,
            fontFamily: 'Caveat, cursive, Arial',
            transform: `rotate(${Math.sin(frame * 0.1) * 0.5}deg)`,
          }}
        >
          {title}
        </div>
      </div>

      {/* 内容列表 */}
      <div
        style={{
          position: 'absolute',
          width: '80%',
          top: '30%',
          left: '10%',
          display: 'flex',
          flexDirection: 'column',
          gap: 40,
        }}
      >
        {content.map((item, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 20,
              opacity: contentProgress[i],
              transform: `translateX(${interpolate(contentProgress[i], [0, 1], [-50, 0])}px)`,
            }}
          >
            {/* 手绘复选框 */}
            <svg width={50} height={50} style={{ flexShrink: 0 }}>
              <path
                d={getHandDrawnPath(5, 25, 45, 25, 3)}
                stroke={primaryColor}
                strokeWidth={3}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={interpolate(contentProgress[i], [0, 1], [100, 0])}
                strokeDashoffset={interpolate(contentProgress[i], [0, 1], [100, 0])}
                style={{
                  filter: `url(#hand-drawn-${i})`,
                }}
              />
              {/* 勾选标记 */}
              {contentProgress[i] > 0.5 && (
                <path
                  d={getHandDrawnPath(10, 25, 20, 35, 2) + ' ' + getHandDrawnPath(20, 35, 40, 15, 2)}
                  stroke={primaryColor}
                  strokeWidth={3}
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={interpolate(contentProgress[i], [0.5, 1], [50, 0])}
                  strokeDashoffset={interpolate(contentProgress[i], [0.5, 1], [50, 0])}
                />
              )}
              <defs>
                <filter id={`hand-drawn-${i}`}>
                  <feTurbulence type="turbulence" baseFrequency="0.05" numOctaves="2" result="noise" />
                  <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" />
                </filter>
              </defs>
            </svg>

            {/* 文字 */}
            <div
              style={{
                fontSize: 48,
                color: primaryColor,
                fontFamily: 'Caveat, cursive, Arial',
                transform: `rotate(${Math.sin(frame * 0.05 + i) * 0.3}deg)`,
              }}
            >
              {item}
            </div>
          </div>
        ))}
      </div>

      {/* 手绘框 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute' }}
      >
        {boxProgress > 0 && (
          <>
            {/* 左上角 */}
            <path
              d={getHandDrawnPath(100, 100, 300, 100, 4)}
              stroke={primaryColor}
              strokeWidth={4}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={200}
              strokeDashoffset={interpolate(boxProgress, [0, 0.25], [200, 0])}
            />
            <path
              d={getHandDrawnPath(100, 100, 100, 300, 4)}
              stroke={primaryColor}
              strokeWidth={4}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={200}
              strokeDashoffset={interpolate(boxProgress, [0.25, 0.5], [200, 0])}
            />

            {/* 右下角 */}
            <path
              d={getHandDrawnPath(width - 300, height - 100, width - 100, height - 100, 4)}
              stroke={primaryColor}
              strokeWidth={4}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={200}
              strokeDashoffset={interpolate(boxProgress, [0.5, 0.75], [200, 0])}
            />
            <path
              d={getHandDrawnPath(width - 100, height - 300, width - 100, height - 100, 4)}
              stroke={primaryColor}
              strokeWidth={4}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={200}
              strokeDashoffset={interpolate(boxProgress, [0.75, 1], [200, 0])}
            />
          </>
        )}
      </svg>

      {/* 装饰性手绘元素 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', opacity: 0.2 }}
      >
        {/* 箭头 */}
        <path
          d={getHandDrawnPath(150, 400, 250, 350, 3)}
          stroke={primaryColor}
          strokeWidth={3}
          fill="none"
          markerEnd="url(#arrow)"
        />

        {/* 星星 */}
        <path
          d={`M ${width - 200} ${200} ${getHandDrawnPath(width - 200, 200, width - 180, 240, 3)}`}
          stroke={primaryColor}
          strokeWidth={3}
          fill="none"
        />

        <defs>
          <marker
            id="arrow"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L0,6 L9,3 z" fill={primaryColor} />
          </marker>
        </defs>
      </svg>
    </AbsoluteFill>
  );
};

// 勾股定理示例（教育内容 - 10s 完整推导）
export const PythagoreanTheorem: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // ---------------- 配色（Excalidraw 风格：米黄纸 + 彩色墨迹） ----------------
  const INK = '#1e1e1e';
  const INK_SOFT = '#3f3f46';
  const COLOR_A = '#ef4444'; // a 边 / a² 正方形 - 红
  const COLOR_B = '#2563eb'; // b 边 / b² 正方形 - 蓝
  const COLOR_C = '#10b981'; // c 斜边 / c² 正方形 - 绿
  const ACCENT = '#f59e0b'; // 强调 / 勾选

  // ---------------- 几何（3-4-5 直角三角形，比例尺 80px/unit） ----------------
  const S = 80;
  const aLen = 3 * S; // 240
  const bLen = 4 * S; // 320

  // 右直角顶点（SVG 坐标，y 向下）
  const P = { x: 520, y: 680 };
  const Atop = { x: P.x, y: P.y - aLen }; // (520, 440)
  const Bright = { x: P.x + bLen, y: P.y }; // (840, 680)

  // 三个正方形的关键坐标
  const sqA = { x: P.x - aLen, y: P.y - aLen, size: aLen }; // (280, 440)
  const sqB = { x: P.x, y: P.y, size: bLen }; // (520, 680)
  // 斜边外法向量 (b, -a)/c，位移 = (b*S, -a*S) = (320, -240)
  const sqC: Array<[number, number]> = [
    [Bright.x, Bright.y], // (840, 680)
    [Atop.x, Atop.y], // (520, 440)
    [Atop.x + bLen, Atop.y - aLen], // (840, 200)
    [Bright.x + bLen, Bright.y - aLen], // (1160, 440)
  ];

  // ---------------- 确定性手绘抖动（不用 Math.random 保证逐帧一致） ----------------
  const jitter = (seed: number, amp = 2) => {
    const n = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
    return (n - Math.floor(n) - 0.5) * 2 * amp;
  };

  const wigglyLine = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    seedBase: number,
    segs = 10,
    amp = 2
  ) => {
    const pts: string[] = [`M ${x1} ${y1}`];
    for (let i = 1; i <= segs; i++) {
      const t = i / segs;
      const x = x1 + (x2 - x1) * t + (i === segs ? 0 : jitter(seedBase + i * 3.1, amp));
      const y = y1 + (y2 - y1) * t + (i === segs ? 0 : jitter(seedBase + i * 7.7, amp));
      pts.push(`L ${x.toFixed(2)} ${y.toFixed(2)}`);
    }
    return pts.join(' ');
  };

  const lineLen = (x1: number, y1: number, x2: number, y2: number) =>
    Math.hypot(x2 - x1, y2 - y1);

  // ---------------- 时间线（300 帧 / 10 秒） ----------------
  const titleIn = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: 'clamp' });
  const underlineIn = interpolate(frame, [15, 35], [0, 1], { extrapolateRight: 'clamp' });

  const drawA = interpolate(frame, [25, 55], [0, 1], { extrapolateRight: 'clamp' });
  const drawB = interpolate(frame, [55, 85], [0, 1], { extrapolateRight: 'clamp' });
  const drawC = interpolate(frame, [85, 120], [0, 1], { extrapolateRight: 'clamp' });
  const rightAngleIn = interpolate(frame, [110, 125], [0, 1], { extrapolateRight: 'clamp' });

  const labelA = spring({ frame: frame - 48, fps, from: 0, to: 1, config: { damping: 11, stiffness: 140 } });
  const labelB = spring({ frame: frame - 78, fps, from: 0, to: 1, config: { damping: 11, stiffness: 140 } });
  const labelC = spring({ frame: frame - 112, fps, from: 0, to: 1, config: { damping: 11, stiffness: 140 } });

  const sqAIn = spring({ frame: frame - 125, fps, from: 0, to: 1, config: { damping: 13, stiffness: 120 } });
  const sqBIn = spring({ frame: frame - 140, fps, from: 0, to: 1, config: { damping: 13, stiffness: 120 } });
  const sqCIn = spring({ frame: frame - 155, fps, from: 0, to: 1, config: { damping: 13, stiffness: 120 } });

  const countA = Math.round(
    interpolate(frame, [145, 175], [0, 9], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
  );
  const countB = Math.round(
    interpolate(frame, [160, 190], [0, 16], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
  );
  const countC = Math.round(
    interpolate(frame, [175, 210], [0, 25], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
  );

  const sumIn = spring({ frame: frame - 215, fps, from: 0, to: 1, config: { damping: 12, stiffness: 120 } });
  const formulaIn = spring({ frame: frame - 255, fps, from: 0, to: 1, config: { damping: 10, stiffness: 110 } });
  const checkIn = interpolate(frame, [275, 295], [0, 1], { extrapolateRight: 'clamp' });

  const pulse = frame > 290 ? 1 + Math.sin((frame - 290) * 0.4) * 0.015 : 1;

  const sumClamp = Math.min(1, sumIn);
  const formulaClamp = Math.min(1, formulaIn);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#fef3c7',
        overflow: 'hidden',
        fontFamily: 'Caveat, "Comic Sans MS", cursive, Arial',
      }}
    >
      {/* 白板网格纹理 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.35,
          backgroundImage:
            'radial-gradient(circle, #00000014 1px, transparent 1.5px), radial-gradient(circle, #00000008 1px, transparent 1px)',
          backgroundSize: '24px 24px, 8px 8px',
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleIn,
          transform: `translateY(${interpolate(titleIn, [0, 1], [-30, 0])}px)`,
        }}
      >
        <div style={{ fontSize: 82, fontWeight: 700, color: INK, letterSpacing: 1 }}>
          勾股定理 · Pythagorean Theorem
        </div>
        <svg width={720} height={24} style={{ display: 'block', margin: '4px auto 0' }}>
          <path
            d={wigglyLine(20, 12, 700, 12, 99, 40, 2.5)}
            stroke={ACCENT}
            strokeWidth={5}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={700}
            strokeDashoffset={interpolate(underlineIn, [0, 1], [700, 0])}
          />
        </svg>
      </div>

      {/* 主画布 */}
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{
          position: 'absolute',
          inset: 0,
          transform: `scale(${pulse})`,
          transformOrigin: '50% 55%',
        }}
      >
        {/* 正方形 a²（左侧，红） */}
        <g
          transform={`translate(${sqA.x + sqA.size / 2} ${sqA.y + sqA.size / 2}) scale(${sqAIn}) translate(${-(sqA.x + sqA.size / 2)} ${-(sqA.y + sqA.size / 2)})`}
          opacity={Math.min(1, sqAIn)}
        >
          <path
            d={`M ${sqA.x} ${sqA.y} L ${sqA.x + sqA.size} ${sqA.y} L ${sqA.x + sqA.size} ${sqA.y + sqA.size} L ${sqA.x} ${sqA.y + sqA.size} Z`}
            fill={COLOR_A}
            fillOpacity={0.18}
            stroke={COLOR_A}
            strokeWidth={4}
            strokeLinejoin="round"
          />
          {[0, 1, 2].map((i) =>
            [0, 1, 2].map((j) => (
              <rect
                key={`a-${i}-${j}`}
                x={sqA.x + i * (sqA.size / 3) + 6}
                y={sqA.y + j * (sqA.size / 3) + 6}
                width={sqA.size / 3 - 12}
                height={sqA.size / 3 - 12}
                fill={COLOR_A}
                fillOpacity={0.15}
                stroke={COLOR_A}
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
            ))
          )}
          <text
            x={sqA.x + sqA.size / 2}
            y={sqA.y + sqA.size / 2 + 14}
            textAnchor="middle"
            fontSize={56}
            fontWeight={700}
            fill={COLOR_A}
          >
            a² = {countA}
          </text>
        </g>

        {/* 正方形 b²（下方，蓝） */}
        <g
          transform={`translate(${sqB.x + sqB.size / 2} ${sqB.y + sqB.size / 2}) scale(${sqBIn}) translate(${-(sqB.x + sqB.size / 2)} ${-(sqB.y + sqB.size / 2)})`}
          opacity={Math.min(1, sqBIn)}
        >
          <path
            d={`M ${sqB.x} ${sqB.y} L ${sqB.x + sqB.size} ${sqB.y} L ${sqB.x + sqB.size} ${sqB.y + sqB.size} L ${sqB.x} ${sqB.y + sqB.size} Z`}
            fill={COLOR_B}
            fillOpacity={0.18}
            stroke={COLOR_B}
            strokeWidth={4}
            strokeLinejoin="round"
          />
          {[0, 1, 2, 3].map((i) =>
            [0, 1, 2, 3].map((j) => (
              <rect
                key={`b-${i}-${j}`}
                x={sqB.x + i * (sqB.size / 4) + 6}
                y={sqB.y + j * (sqB.size / 4) + 6}
                width={sqB.size / 4 - 12}
                height={sqB.size / 4 - 12}
                fill={COLOR_B}
                fillOpacity={0.15}
                stroke={COLOR_B}
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
            ))
          )}
          <text
            x={sqB.x + sqB.size / 2}
            y={sqB.y + sqB.size / 2 + 16}
            textAnchor="middle"
            fontSize={64}
            fontWeight={700}
            fill={COLOR_B}
          >
            b² = {countB}
          </text>
        </g>

        {/* 正方形 c²（斜边外侧，绿，旋转） */}
        <g
          transform={`translate(${(sqC[0][0] + sqC[2][0]) / 2} ${(sqC[0][1] + sqC[2][1]) / 2}) scale(${sqCIn}) translate(${-((sqC[0][0] + sqC[2][0]) / 2)} ${-((sqC[0][1] + sqC[2][1]) / 2)})`}
          opacity={Math.min(1, sqCIn)}
        >
          <polygon
            points={sqC.map((p) => p.join(',')).join(' ')}
            fill={COLOR_C}
            fillOpacity={0.2}
            stroke={COLOR_C}
            strokeWidth={4}
            strokeLinejoin="round"
          />
          {[1, 2, 3, 4].map((i) => {
            const t = i / 5;
            const la: [number, number] = [
              sqC[0][0] + (sqC[3][0] - sqC[0][0]) * t,
              sqC[0][1] + (sqC[3][1] - sqC[0][1]) * t,
            ];
            const lb: [number, number] = [
              sqC[1][0] + (sqC[2][0] - sqC[1][0]) * t,
              sqC[1][1] + (sqC[2][1] - sqC[1][1]) * t,
            ];
            const ma: [number, number] = [
              sqC[0][0] + (sqC[1][0] - sqC[0][0]) * t,
              sqC[0][1] + (sqC[1][1] - sqC[0][1]) * t,
            ];
            const mb: [number, number] = [
              sqC[3][0] + (sqC[2][0] - sqC[3][0]) * t,
              sqC[3][1] + (sqC[2][1] - sqC[3][1]) * t,
            ];
            return (
              <g
                key={`cg-${i}`}
                stroke={COLOR_C}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                opacity={0.5}
              >
                <line x1={la[0]} y1={la[1]} x2={lb[0]} y2={lb[1]} />
                <line x1={ma[0]} y1={ma[1]} x2={mb[0]} y2={mb[1]} />
              </g>
            );
          })}
          <text
            x={(sqC[0][0] + sqC[2][0]) / 2}
            y={(sqC[0][1] + sqC[2][1]) / 2 + 18}
            textAnchor="middle"
            fontSize={72}
            fontWeight={700}
            fill={COLOR_C}
          >
            c² = {countC}
          </text>
        </g>

        {/* a 边（红） */}
        <path
          d={wigglyLine(P.x, P.y, Atop.x, Atop.y, 11, 10, 2)}
          stroke={COLOR_A}
          strokeWidth={7}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={lineLen(P.x, P.y, Atop.x, Atop.y) + 20}
          strokeDashoffset={interpolate(
            drawA,
            [0, 1],
            [lineLen(P.x, P.y, Atop.x, Atop.y) + 20, 0]
          )}
        />
        {/* b 边（蓝） */}
        <path
          d={wigglyLine(P.x, P.y, Bright.x, Bright.y, 22, 10, 2)}
          stroke={COLOR_B}
          strokeWidth={7}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={lineLen(P.x, P.y, Bright.x, Bright.y) + 20}
          strokeDashoffset={interpolate(
            drawB,
            [0, 1],
            [lineLen(P.x, P.y, Bright.x, Bright.y) + 20, 0]
          )}
        />
        {/* c 斜边（绿） */}
        <path
          d={wigglyLine(Bright.x, Bright.y, Atop.x, Atop.y, 33, 12, 2)}
          stroke={COLOR_C}
          strokeWidth={7}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={lineLen(Bright.x, Bright.y, Atop.x, Atop.y) + 20}
          strokeDashoffset={interpolate(
            drawC,
            [0, 1],
            [lineLen(Bright.x, Bright.y, Atop.x, Atop.y) + 20, 0]
          )}
        />

        {/* 直角标记 */}
        {rightAngleIn > 0 && (
          <g opacity={rightAngleIn}>
            <path
              d={`M ${P.x + 22} ${P.y} L ${P.x + 22} ${P.y - 22} L ${P.x} ${P.y - 22}`}
              stroke={INK}
              strokeWidth={3}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx={P.x + 11} cy={P.y - 11} r={2.5} fill={INK} />
          </g>
        )}

        {/* 边长标签 */}
        <g
          opacity={labelA}
          transform={`translate(${P.x - 42}, ${(P.y + Atop.y) / 2 + 10}) scale(${labelA})`}
        >
          <circle cx={0} cy={-8} r={22} fill="#fff" stroke={COLOR_A} strokeWidth={3} />
          <text textAnchor="middle" y={4} fontSize={32} fontWeight={700} fill={COLOR_A}>
            a
          </text>
        </g>
        <g
          opacity={labelB}
          transform={`translate(${(P.x + Bright.x) / 2}, ${P.y + 48}) scale(${labelB})`}
        >
          <circle cx={0} cy={-8} r={22} fill="#fff" stroke={COLOR_B} strokeWidth={3} />
          <text textAnchor="middle" y={4} fontSize={32} fontWeight={700} fill={COLOR_B}>
            b
          </text>
        </g>
        <g
          opacity={labelC}
          transform={`translate(${(Bright.x + Atop.x) / 2 + 36}, ${(Bright.y + Atop.y) / 2 - 18}) scale(${labelC})`}
        >
          <circle cx={0} cy={-8} r={22} fill="#fff" stroke={COLOR_C} strokeWidth={3} />
          <text textAnchor="middle" y={4} fontSize={32} fontWeight={700} fill={COLOR_C}>
            c
          </text>
        </g>
      </svg>

      {/* 右侧公式推导面板 */}
      <div
        style={{
          position: 'absolute',
          top: 260,
          right: 80,
          width: 560,
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
        }}
      >
        {/* 中间等式 9 + 16 = 25 */}
        <div
          style={{
            background: '#ffffffcc',
            border: `3px dashed ${INK}`,
            borderRadius: 18,
            padding: '24px 28px',
            opacity: sumClamp,
            transform: `translateY(${interpolate(sumClamp, [0, 1], [30, 0])}px) rotate(${interpolate(sumClamp, [0, 1], [-2, 0])}deg)`,
            boxShadow: '6px 6px 0 #00000018',
          }}
        >
          <div style={{ fontSize: 30, color: INK_SOFT, marginBottom: 6 }}>面积关系</div>
          <div style={{ fontSize: 64, fontWeight: 700, letterSpacing: 1 }}>
            <span style={{ color: COLOR_A }}>9</span>
            <span style={{ color: INK, margin: '0 10px' }}>+</span>
            <span style={{ color: COLOR_B }}>16</span>
            <span style={{ color: INK, margin: '0 10px' }}>=</span>
            <span style={{ color: COLOR_C }}>25</span>
          </div>
        </div>

        {/* 最终公式 a² + b² = c² */}
        <div
          style={{
            background: '#fff',
            border: `3px solid ${INK}`,
            borderRadius: 20,
            padding: '32px 28px',
            opacity: formulaClamp,
            transform: `translateY(${interpolate(formulaClamp, [0, 1], [30, 0])}px) scale(${0.85 + 0.15 * formulaClamp})`,
            boxShadow: `8px 8px 0 ${ACCENT}`,
            position: 'relative',
          }}
        >
          <div style={{ fontSize: 30, color: INK_SOFT, marginBottom: 10 }}>
            勾股定理 · Pythagoras ≈ 500 BC
          </div>
          <div style={{ fontSize: 84, fontWeight: 700, textAlign: 'center', letterSpacing: 2 }}>
            <span style={{ color: COLOR_A }}>a²</span>
            <span style={{ color: INK, margin: '0 12px' }}>+</span>
            <span style={{ color: COLOR_B }}>b²</span>
            <span style={{ color: INK, margin: '0 12px' }}>=</span>
            <span style={{ color: COLOR_C }}>c²</span>
          </div>

          {checkIn > 0 && (
            <svg
              width={120}
              height={120}
              style={{
                position: 'absolute',
                right: -32,
                top: -36,
                transform: `rotate(${interpolate(checkIn, [0, 1], [-30, 10])}deg)`,
              }}
            >
              <circle
                cx={60}
                cy={60}
                r={48}
                fill={ACCENT}
                stroke={INK}
                strokeWidth={4}
                opacity={checkIn}
              />
              <path
                d="M 32 62 L 54 82 L 90 42"
                stroke={INK}
                strokeWidth={8}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={120}
                strokeDashoffset={interpolate(checkIn, [0, 1], [120, 0])}
              />
            </svg>
          )}
        </div>
      </div>

      {/* 左下角说明 */}
      <div
        style={{
          position: 'absolute',
          right: 50,
          bottom: 50,
          fontSize: 28,
          color: INK_SOFT,
          opacity: interpolate(frame, [180, 220], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
          maxWidth: 520,
          lineHeight: 1.4,
        }}
      >
        在直角三角形中，两直角边平方之和等于斜边的平方。
        <br />
        <span style={{ color: INK_SOFT, fontSize: 22 }}>
          In a right triangle, the sum of the squares of the legs equals the square of the hypotenuse.
        </span>
      </div>
    </AbsoluteFill>
  );
};
